import { useCallback, useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { getTask, reportTask } from '../api/tasks';
import { acceptTask } from '../api/submissions';
import { ReviewTask } from '../types';
import { Button, Card, ErrorText, FormInput, Label, LoadingState, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';
import { openDirections } from '../utils/openUrl';
import { resolvePhotoUrl } from '../config';

/** Matches backend OWNER_REJECTION_WARN_THRESHOLD_BP — shown as a caution,
 * not a hard block, since the reviewer still decides for themselves. */
const OWNER_REJECTION_WARN_THRESHOLD_BP = 3000;

export default function TaskDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { token } = useAuth();
  const router = useRouter();
  const [task, setTask] = useState<ReviewTask | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      setTask(await getTask(token, id));
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được nhiệm vụ, vui lòng thử lại.');
    }
  }, [token, id]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onAccept = async () => {
    if (!token || !id) return;
    setError(null);
    setAccepting(true);
    try {
      const submission = await acceptTask(token, id);
      router.replace(`/submission/${submission.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Không nhận được nhiệm vụ — có thể đã hết lượt');
    } finally {
      setAccepting(false);
    }
  };

  if (!task) {
    if (loadError) {
      return (
        <ScreenContainer>
          <ErrorText>{loadError}</ErrorText>
          <Button title="Thử lại" onPress={load} />
        </ScreenContainer>
      );
    }
    return <LoadingState />;
  }

  const remaining = task.totalSlots - task.filledSlots;
  const showRejectionWarning =
    (task.ownerTotalDecided ?? 0) >= 3 &&
    (task.ownerRejectionRateBp ?? 0) >= OWNER_REJECTION_WARN_THRESHOLD_BP;

  const onSendReport = async () => {
    if (!token || !id || !reportReason.trim()) return;
    setReporting(true);
    setError(null);
    try {
      await reportTask(token, id, reportReason.trim());
      setReportSent(true);
      setShowReportForm(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Gửi báo cáo thất bại');
    } finally {
      setReporting(false);
    }
  };

  return (
    <ScreenContainer>
      <ScrollView>
        <Card>
          <Text style={{ fontSize: 18, fontFamily: fonts.bold, color: colors.text }}>{task.title}</Text>
          <Text style={{ color: colors.textMuted, marginTop: 4 }}>{task.shop?.name}</Text>
          <Text style={{ color: colors.textMuted }}>{task.shop?.address}</Text>
          {task.shop?.avgRating != null ? (
            <Text style={{ color: colors.textMuted, marginTop: 4 }}>
              ★ {task.shop.avgRating.toFixed(1)} ({task.shop.ratingCount} lượt đánh giá từ người làm nhiệm vụ)
            </Text>
          ) : null}
          <Text style={{ color: task.requiresPurchase ? colors.warning : colors.success, marginTop: 6, fontSize: 13, fontFamily: fonts.semiBold }}>
            {task.requiresPurchase ? 'Yêu cầu mua hoặc sử dụng dịch vụ tại shop' : 'Không cần mua gì'}
          </Text>

          {task.shop ? (
            <Button
              title="Chỉ đường đến shop"
              variant="outline"
              onPress={() => openDirections(task.shop!.latitude, task.shop!.longitude)}
            />
          ) : null}
        </Card>

        {showRejectionWarning ? (
          <Card style={{ borderColor: colors.warning, borderWidth: 1 }}>
            <Text style={{ color: colors.warning, fontFamily: fonts.bold, marginBottom: 4 }}>
              Cảnh báo: shop này có tỷ lệ từ chối bài nộp cao
            </Text>
            <Text style={{ color: colors.textMuted, fontSize: 13 }}>
              Khoảng {Math.round((task.ownerRejectionRateBp ?? 0) / 100)}% bài nộp gần đây của shop này
              bị từ chối. Bạn nên cân nhắc kỹ trước khi nhận nhiệm vụ này — nếu bị từ chối mà bạn cho là
              không thoả đáng, bạn có thể gửi khiếu nại sau khi nộp bài.
            </Text>
          </Card>
        ) : null}

        <Card style={{ backgroundColor: colors.background }}>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
            Lưu ý an toàn
          </Text>
          <Text style={{ color: colors.textMuted, fontSize: 13 }}>
            Chỉ nhận nhiệm vụ tại địa điểm kinh doanh có thật (đã gắn liên kết Google Maps), và nên đến
            trong giờ hoạt động của shop. Nếu nhiệm vụ có dấu hiệu bất thường hoặc chủ shop yêu cầu điều
            gì ngoài mô tả, hãy dừng lại và báo cáo.
          </Text>
        </Card>

        <Card>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>Mô tả</Text>
          <Text style={{ color: colors.text }}>{task.description}</Text>
        </Card>

        <Card>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
            Hướng dẫn thực hiện
          </Text>
          <Text style={{ color: colors.text }}>{task.instructions}</Text>
        </Card>

        {task.estimatedHours ? (
          <Card>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 4 }}>
              Số giờ cần: {task.estimatedHours} giờ
            </Text>
          </Card>
        ) : null}

        {task.destinationAddress ? (
          <Card>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
              Địa chỉ cần chuyển đến
            </Text>
            <Text style={{ color: colors.text, marginBottom: task.photoUrl ? 10 : 0 }}>
              {task.destinationAddress}
            </Text>
            {task.photoUrl ? (
              <>
                <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
                  Ảnh đồ cần chuyển
                </Text>
                <Image
                  source={{ uri: resolvePhotoUrl(task.photoUrl) }}
                  style={{ width: 120, height: 120, borderRadius: 8 }}
                />
              </>
            ) : null}
          </Card>
        ) : null}

        {task.advanceAmountVnd ? (
          <Card style={{ borderColor: colors.warning, borderWidth: 1 }}>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 4 }}>
              Số tiền tối đa cần ứng trước: {task.advanceAmountVnd.toLocaleString('vi-VN')}đ
            </Text>
            <Text style={{ color: colors.textMuted, fontSize: 12 }}>
              Chủ shop hoàn lại số tiền này trực tiếp cho bạn, ngoài ứng dụng, sau khi bạn mua đồ và nộp
              hoá đơn/ảnh chứng từ. Loot không giữ và không xử lý tranh chấp phát sinh từ khoản ứng tiền
              này — chỉ nhận nhiệm vụ này nếu bạn tin tưởng chủ shop.
            </Text>
          </Card>
        ) : null}

        {task.referralPercentTerms ? (
          <Card style={{ borderColor: colors.warning, borderWidth: 1 }}>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 4 }}>
              Thoả thuận % đơn hàng (thoả thuận riêng với chủ shop)
            </Text>
            <Text style={{ color: colors.text }}>{task.referralPercentTerms}</Text>
            <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
              Đây là thoả thuận riêng, ngoài số xu thưởng cố định bên dưới. Loot không tính, không giữ và
              không xử lý tranh chấp phát sinh từ thoả thuận này — nên thoả thuận rõ ràng và có mặt trực
              tiếp khi thực hiện để tránh tranh chấp về sau.
            </Text>
          </Card>
        ) : null}

        <Card>
          <Row label="Phần thưởng" value={`${task.rewardCoins} xu`} />
          <Row label="Số ảnh cần chụp" value={`${task.requiredPhotos} ảnh`} />
          <Row label="Bán kính check-in" value={`${task.checkinRadiusMeters}m`} />
          <Row label="Còn lại" value={`${remaining}/${task.totalSlots} lượt`} />
        </Card>

        <ErrorText>{error}</ErrorText>

        <Button
          title="Nhận nhiệm vụ này"
          onPress={onAccept}
          loading={accepting}
          disabled={remaining <= 0 || task.status !== 'ACTIVE'}
        />

        {reportSent ? (
          <Text style={{ color: colors.success, textAlign: 'center', marginTop: 12, fontSize: 13 }}>
            Đã gửi báo cáo, admin sẽ xem xét nhiệm vụ này.
          </Text>
        ) : showReportForm ? (
          <Card style={{ marginTop: 12 }}>
            <Label>Lý do báo cáo nhiệm vụ đáng ngờ</Label>
            <FormInput
              value={reportReason}
              onChangeText={setReportReason}
              placeholder="Ví dụ: địa chỉ không khớp với Google Maps, yêu cầu bất thường"
              multiline
            />
            <Button
              title="Gửi báo cáo"
              variant="danger"
              onPress={onSendReport}
              loading={reporting}
              disabled={reportReason.trim().length < 5}
            />
          </Card>
        ) : (
          <Pressable onPress={() => setShowReportForm(true)} style={{ marginTop: 14, alignItems: 'center' }}>
            <Text style={{ color: colors.textMuted, fontSize: 13, textDecorationLine: 'underline' }}>
              Báo cáo nhiệm vụ này đáng ngờ
            </Text>
          </Pressable>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
      <Text style={{ color: colors.textMuted }}>{label}</Text>
      <Text style={{ color: colors.text, fontFamily: fonts.semiBold }}>{value}</Text>
    </View>
  );
}
